import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Player } from '@lottiefiles/react-lottie-player';
import { useProfile } from '@/api/queries/useProfile';
import { useAuthStore } from '@/stores/auth-store';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  Button,
  Card,
  CardContent,
  Page,
  PageContent,
  getUserInitials,
  getUserAvatarUrl
} from '@bliks-io/ui';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface LandingPageProps {
  onContinue?: () => void;
}

export function LandingPage({ onContinue }: LandingPageProps) {
  const { t, i18n } = useTranslation();
  useDocumentTitle('pageTitles.home');
  const slug = useAuthStore().slug();
  const { data: user, isLoading } = useProfile();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  const language = i18n.language || 'en';

  return (
    <Page>
      <PageContent>
        {/* Header Section */}
        <header className="flex flex-col items-center mb-12">
          <Avatar size="lg" className="mb-6">
            <AvatarImage
              src={getUserAvatarUrl(user, 'medium')}
              alt={`${user?.firstName} ${user?.lastName}`}
            />
            <AvatarFallback className="text-3xl font-semibold bg-muted">
              {getUserInitials(user?.firstName, user?.lastName)}
            </AvatarFallback>
          </Avatar>
          <div className="text-center">
            <h1 className="mb-2 text-4xl font-bold">
              {t('welcomePage.welcome')}, {user?.firstName} {user?.lastName}
            </h1>
          </div>
        </header>

        <div className="flex justify-center gap-4 mb-12">
          <Button variant="primary" size="large" asChild>
            <Link to="/$slug/my-tasks" params={{ slug }}>
              {t('welcomePage.checkTask')}
            </Link>
          </Button>
          {onContinue && (
            <Button variant="outline" size="large" onClick={onContinue}>
              {t('welcomePage.continueToDashboard')}
            </Button>
          )}
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 gap-8 mb-16 md:grid-cols-3">
          {/* Feature Card 1: Minimize Volume */}
          <Card className="flex flex-col items-center p-6 text-center">
            <div className="flex items-center justify-center w-full h-64 mb-4">
              <Player
                src="/assets/player/Lottie Virale Prozess.json"
                background="transparent"
                speed={1}
                loop
                autoplay
                style={{ height: '250px' }}
              />
            </div>
            <CardContent className="p-0">
              <h2 className="mb-3 text-xl font-semibold">{t('welcomePage.minimizeVolume')}</h2>
              <p className="text-muted-foreground">{t('welcomePage.minimizeVolumeDesc')}</p>
            </CardContent>
          </Card>

          {/* Feature Card 2: All Resources in One */}
          <Card className="flex flex-col items-center p-6 text-center">
            <div className="flex items-center justify-center w-full h-64 mb-4">
              <Player
                src="/assets/player/Lottie Apps.json"
                background="transparent"
                speed={1}
                loop
                autoplay
                style={{ height: '250px' }}
              />
            </div>
            <CardContent className="p-0">
              <h2 className="mb-3 text-xl font-semibold">{t('welcomePage.allResourceInOne')}</h2>
              <p className="text-muted-foreground">{t('welcomePage.allResourceInOneDesc')}</p>
            </CardContent>
          </Card>

          {/* Feature Card 3: Complete Process */}
          <Card className="flex flex-col items-center p-6 text-center">
            <div className="flex items-center justify-center w-full h-64 mb-4">
              <Player
                src="/assets/player/Lottie BPMN.json"
                background="transparent"
                speed={1}
                loop
                autoplay
                style={{ height: '250px' }}
              />
            </div>
            <CardContent className="p-0">
              <h2 className="mb-3 text-xl font-semibold">{t('welcomePage.completeProcess')}</h2>
              <p className="text-muted-foreground">{t('welcomePage.completeProcessDesc')}</p>
            </CardContent>
          </Card>
        </div>

        {/* How It Works Section */}
        <section className="p-8 bg-muted rounded-xl">
          <h2 className="mb-6 text-2xl font-semibold text-center">
            {t('welcomePage.howRecordingWork')}
          </h2>
          <div className="flex justify-center">
            <Player
              src={`/assets/player/Lottie Wie funktioniert Virale Prozessaufnahme${language === 'en' ? '_EN' : ''}.json`}
              background="transparent"
              speed={1}
              loop
              autoplay
              style={{ height: '300px', width: '100%', maxWidth: '800px' }}
            />
          </div>
        </section>
      </PageContent>
    </Page>
  );
}
